import { Ability } from "@/interfaces/ability";
import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import axios from "@/lib/axios";
import { useUserAbilitiesStore } from "@/stores/userAbilities";
import { type QueryKey, useMutation, useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { type ComputedRef, type Ref, computed, watch } from "vue";
import { ToastInterface, useToast } from "vue-toastification";

interface Params {
  userId: string | number;
  companyId?: ComputedRef<number | undefined> | Ref<number | undefined>;
}

interface Props {
  params: Params;
  immediate?: boolean;
}

interface CreateParams {
  userId: string | number;
  abilities: number[];
  toast: ToastInterface;
}

const getUserAbilities = async (
  userId: string,
  companyId?: number,
): Promise<Ability[]> => {
  const { data } = await axios.get<Ability[]>(`api/users/${userId}/abilities`, {
    params: companyId ? { companyId } : undefined,
  });
  return data;
};

const postUserAbility = async (params: CreateParams) => {
  try {
    await axios.post(
      `api/users/${params.userId}/abilities`,
      { abilities: params.abilities },
      { errorHandling: false } as CustomAxiosRequestConfig,
    );
    params.toast.success("Permisos actualizados correctamente");
  } catch (error: any) {
    params.toast.error(
      error?.response?.data?.message ??
        "No tienes permiso para otorgar estos permisos",
    );
  }
};

const useUserAbilities = (props: Props) => {
  const { immediate = true, params } = props;
  const { setUserAbilities } = useUserAbilitiesStore();
  const { userAbilities } = storeToRefs(useUserAbilitiesStore());
  const toast = useToast();

  const queryKey = computed((): QueryKey => [
    "userAbilities",
    params.userId,
    params.companyId?.value,
  ]);

  const isEnabled = computed(() =>
    params.companyId ? !!params.companyId.value : !!immediate,
  );

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      getUserAbilities(params.userId.toString(), params.companyId?.value),
    enabled: isEnabled,
  });

  const postUserAbilityMutation = useMutation((p: CreateParams) =>
    postUserAbility({ abilities: p.abilities, userId: p.userId, toast }),
  );

  watch(
    data,
    (value) => {
      if (value) {
        setUserAbilities(
          value.map((ability) => ({
            ...ability,
            checked: ability.isActive,
          })),
        );
      }
    },
    { immediate },
  );

  return {
    userAbilities,
    isLoading,
    postUserAbility: postUserAbilityMutation.mutate,
    postUserAbilityAsync: postUserAbilityMutation.mutateAsync,
    postUserAbilityLoading: postUserAbilityMutation.isLoading,
  };
};

export default useUserAbilities;
